import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, Empty, Failure, Field, Loading, Notice } from '../components/ui.js';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  school_count: number;
  device_count: number;
}

interface Health {
  status: string;
  checks: Record<string, { ok: boolean; detail?: string }>;
}

const CHECK_LABELS: Record<string, string> = {
  database: 'قاعدة البيانات',
  mqtt: 'وسيط الرسائل',
  outbox: 'صندوق الإرسال',
};

export function SettingsPage(): JSX.Element {
  const { me, can } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

  const tenantId = me?.active_tenant_id;

  const tenant = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => get<Tenant>(`/tenants/${tenantId}`),
    enabled: Boolean(tenantId),
  });

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => get<Health>('/health/ready'),
    refetchInterval: 20_000,
    retry: false,
  });

  useEffect(() => {
    if (tenant.data) {
      setName(tenant.data.name);
      setTimezone((tenant.data.settings.timezone as string) ?? 'Asia/Baghdad');
    }
  }, [tenant.data]);

  const save = useMutation({
    mutationFn: () => patch(`/tenants/${tenantId}`, { name, timezone }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const changePassword = useMutation({
    mutationFn: () =>
      post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      setPasswordMessage('تم تغيير كلمة المرور. أُنهيت كل الجلسات الأخرى؛ قد تحتاج لتسجيل الدخول مجددًا.');
      setCurrentPassword('');
      setNewPassword('');
    },
    onError: (e: Error) => setPasswordMessage(e.message),
  });

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? <Notice tone="info">حُفظت إعدادات المديرية.</Notice> : null}

      <Card title="حالة الخدمة">
        {health.isLoading ? (
          <Loading />
        ) : health.error ? (
          <Failure error={health.error} onRetry={() => void health.refetch()} />
        ) : (
          <div className="grid cols-3">
            {Object.entries(health.data!.checks).map(([key, check]) => (
              <div className="stat" key={key}>
                <div className="label">{CHECK_LABELS[key] ?? key}</div>
                <div className="value" style={{ fontSize: 18 }}>
                  {check.ok ? (
                    <Badge tone="ok" icon="✔">
                      سليم
                    </Badge>
                  ) : (
                    <Badge tone="danger" icon="✖">
                      خلل
                    </Badge>
                  )}
                </div>
                {check.detail ? (
                  <div className="label" style={{ marginTop: 4 }}>
                    {check.detail}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        <Notice tone="info">
          انقطاع وسيط الرسائل لا يوقف الجداول: الأجهزة تنفّذ من نسخة محلية وتصالح الحالة عبر HTTPS
          عند العودة.
        </Notice>
      </Card>

      <Card title="إعدادات المديرية">
        {tenant.isLoading ? (
          <Loading />
        ) : tenant.error ? (
          <Failure error={tenant.error} />
        ) : !tenant.data ? (
          <Empty title="لا توجد مديرية نشطة" />
        ) : (
          <>
            <div className="grid cols-3" style={{ marginBottom: 12 }}>
              <div className="stat">
                <div className="label">المدارس</div>
                <div className="value num">{tenant.data.school_count}</div>
              </div>
              <div className="stat">
                <div className="label">الأجهزة المفعّلة</div>
                <div className="value num">{tenant.data.device_count}</div>
              </div>
              <div className="stat">
                <div className="label">المعرّف المختصر</div>
                <div className="value mono" style={{ fontSize: 18 }}>
                  {tenant.data.slug}
                </div>
              </div>
            </div>

            <div className="grid cols-2">
              <Field label="اسم المديرية">
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setSaved(false);
                  }}
                  disabled={!can('tenant.manage')}
                />
              </Field>
              <Field
                label="المنطقة الزمنية الافتراضية"
                hint="اسم IANA. المدارس يمكنها تجاوزها فرديًا."
              >
                <input
                  className="mono"
                  value={timezone}
                  onChange={(e) => {
                    setTimezone(e.target.value);
                    setSaved(false);
                  }}
                  disabled={!can('tenant.manage')}
                />
              </Field>
            </div>

            {can('tenant.manage') ? (
              <button className="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'جارٍ الحفظ…' : 'حفظ'}
              </button>
            ) : null}
          </>
        )}
      </Card>

      <Card title="أصوات الطوارئ المعتمدة">
        <Notice tone="warn">
          ملفات الإخلاء والطوارئ يرفعها المشغّل ويعتمدها من مكتبة الأصوات، ثم تُحدَّد هنا. النظام لا
          يولّد رسالة إخلاء ولا يسمّي نغمة تجريبية «رسالة معتمدة». تُشحن هذه الملفات ضمن بيان كل
          جهاز لتكون متاحة محليًا وقت الحاجة.
        </Notice>
        <p style={{ color: 'var(--muted)', fontSize: 13.5 }}>
          يُضبط الملف المعتمد حاليًا من إعدادات المديرية في قاعدة البيانات
          (<span className="mono">emergency_evacuation_asset_id</span> و
          <span className="mono">emergency_general_asset_id</span>)، ويجب أن يكون الملف في حالة
          «جاهز» قبل النشر.
        </p>
      </Card>

      <Card title="كلمة المرور">
        {passwordMessage ? <Notice tone="info">{passwordMessage}</Notice> : null}
        <div className="grid cols-2">
          <Field label="كلمة المرور الحالية">
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Field>
          <Field label="كلمة المرور الجديدة" hint="12 محرفًا على الأقل">
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Field>
        </div>
        <button
          disabled={changePassword.isPending || newPassword.length < 12 || !currentPassword}
          onClick={() => changePassword.mutate()}
        >
          {changePassword.isPending ? 'جارٍ التغيير…' : 'تغيير كلمة المرور'}
        </button>
      </Card>

      <Card title="النسخ الاحتياطي والاستعادة">
        <Notice tone="info">
          النسخ والاستعادة يُنفَّذان من الخادم عبر{' '}
          <span className="mono">./scripts/backup.sh</span> و{' '}
          <span className="mono">./scripts/restore.sh</span>. تشمل النسخة قاعدة البيانات والأصول
          الصوتية والإعدادات ومفاتيح التوقيع مشفّرة ومخزّنة منفصلة عن التطبيق. اختبر الاستعادة في
          بيئة جديدة قبل الاعتماد عليها.
        </Notice>
      </Card>
    </>
  );
}
